import { useState, useEffect, useMemo } from "react";
import { Plus, Search, Anchor, Users, Dog, Phone, MessageCircle, Instagram, Facebook, Mail, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { openExternal } from "@/lib/platform";
import CruiserContactModal from "@/components/CruiserContactModal";

/**
 * CruiserContactsView — shared inner UI for the cruiser-contacts feature.
 *
 * Extracted Feb 8, 2026 from `/pages/CruiserContacts.jsx` so the standalone
 * page AND the new Dashboard "Contacts" tab can share exactly the same
 * grid/search/modal wiring. Rendering variants live in the callers; this
 * component owns:
 *   - fetching /cruiser-contacts on mount
 *   - client-side search filtering
 *   - the responsive card grid
 *   - the add/edit modal (CruiserContactModal)
 *
 * Props:
 *   heading    — optional bold section heading (e.g. "People you've met on
 *                the water"). Omit to render just the grid + search.
 *   subheading — optional muted line under the heading.
 *   compact    — when true, drops the eyebrow / heading / subhead entirely
 *                (best for embedding inside a Dashboard tab where the page
 *                already has its own chrome).
 */
export default function CruiserContactsView({
    heading,
    subheading,
    compact = false,
}) {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [search, setSearch] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get("/cruiser-contacts");
                if (alive) setContacts(res.data || []);
            } catch (e) {
                if (alive) setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't load contacts.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return contacts;
        return contacts.filter((c) => {
            const hay = [
                c.boat_name, c.boat_type, c.home_port, c.where_met, c.notes,
                ...(c.people || []).map((p) => p.name),
                ...(c.pets || []).map((p) => p.name),
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [contacts, search]);

    const openNew = () => { setEditing(null); setModalOpen(true); };
    const openEdit = (c) => { setEditing(c); setModalOpen(true); };

    const onSaved = (saved) => {
        setContacts((prev) => {
            const idx = prev.findIndex((c) => c.id === saved.id);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = saved;
                return copy;
            }
            return [saved, ...prev];
        });
    };

    const onDeleted = (id) => {
        setContacts((prev) => prev.filter((c) => c.id !== id));
    };

    return (
        <div data-testid="cruiser-contacts-view">
            {!compact && (heading || subheading) && (
                <div style={{ margin: "0 0 22px" }}>
                    <div style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                        color: "var(--bm-gold)", marginBottom: 8,
                    }}>
                        <Anchor size={12} /> Cruiser contacts
                    </div>
                    {heading && (
                        <h1 className="bm-serif" style={{ margin: 0, fontSize: 32, fontWeight: 500 }}>
                            {heading}
                        </h1>
                    )}
                    {subheading && (
                        <p style={{ margin: "10px 0 0", color: "var(--bm-text-muted)", fontSize: 14, maxWidth: 620 }}>
                            {subheading}
                        </p>
                    )}
                </div>
            )}

            {/* Toolbar row: search + Add-contact button */}
            <div style={{
                display: "flex", gap: 12, flexWrap: "wrap",
                alignItems: "center", marginBottom: 22,
            }}>
                {contacts.length > 0 && (
                    <div style={{ position: "relative", flex: "1 1 300px", maxWidth: 480 }}>
                        <Search size={14} style={{
                            position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                            color: "var(--bm-text-dim)", pointerEvents: "none",
                        }} />
                        <input
                            data-testid="cruiser-contacts-search-input"
                            className="bm-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search boat, person, pet, port…"
                            style={{ paddingLeft: 38, width: "100%" }}
                        />
                    </div>
                )}
                <button
                    data-testid="cruiser-contacts-add-btn"
                    onClick={openNew}
                    className="bm-btn bm-btn-gold"
                    style={{ padding: "10px 18px" }}
                    type="button"
                >
                    <Plus size={14} /> New contact
                </button>
            </div>

            {loading && (
                <div style={{ padding: 60, textAlign: "center", color: "var(--bm-text-muted)" }}>
                    <Loader2 size={20} className="bm-spin" /> Hauling in the contacts…
                </div>
            )}

            {!loading && err && (
                <div data-testid="cruiser-contacts-error" style={{
                    padding: 16, borderRadius: 8,
                    border: "1px solid rgba(231, 111, 81, 0.4)",
                    background: "rgba(231, 111, 81, 0.08)",
                    color: "#e76f51", fontSize: 14,
                }}>{err}</div>
            )}

            {!loading && !err && contacts.length === 0 && (
                <EmptyState onAdd={openNew} />
            )}

            {!loading && !err && filtered.length === 0 && contacts.length > 0 && (
                <p style={{ color: "var(--bm-text-muted)", fontSize: 14 }}>
                    No contacts match &ldquo;{search}&rdquo;.
                </p>
            )}

            {!loading && !err && filtered.length > 0 && (
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: 14,
                }}>
                    {filtered.map((c) => (
                        <ContactCard key={c.id} contact={c} onClick={() => openEdit(c)} />
                    ))}
                </div>
            )}

            <CruiserContactModal
                open={modalOpen}
                contact={editing}
                onClose={() => setModalOpen(false)}
                onSaved={onSaved}
                onDeleted={onDeleted}
            />
        </div>
    );
}

// ---------------- Helpers ----------------
function hullSwatchStyle(primary, trim) {
    const p = primary?.trim().toLowerCase() || "#28384f";
    const t = trim?.trim().toLowerCase() || null;
    return {
        primary: cssColorOrFallback(p, "#3a4d6a"),
        trim: t ? cssColorOrFallback(t, "var(--bm-gold)") : null,
    };
}

function cssColorOrFallback(name, fallback) {
    const safe = /^[a-z]+$|^#[0-9a-f]{3,8}$|^rgb/i;
    return safe.test(name) ? name : fallback;
}

function ContactCard({ contact, onClick }) {
    const people = contact.people || [];
    const pets = contact.pets || [];
    const swatch = hullSwatchStyle(contact.hull_color_primary, contact.hull_color_trim);
    const linked = contact.linked_profile || null;
    const displayPhoto = contact.boat_photo || linked?.hero_photo_url || null;

    return (
        <button
            data-testid={`cruiser-contact-card-${contact.id}`}
            type="button"
            onClick={onClick}
            className="bm-card"
            style={{
                textAlign: "left",
                padding: 0,
                cursor: "pointer",
                border: "1px solid var(--bm-border-soft)",
                background: "var(--bm-card-bg, rgba(255,255,255,0.02))",
                overflow: "hidden",
                transition: "transform 0.15s ease, border-color 0.15s ease",
                display: "flex", flexDirection: "column",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--bm-gold-dim)";
                e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--bm-border-soft)";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            {displayPhoto ? (
                <div
                    data-testid={`cruiser-contact-photo-${contact.id}`}
                    style={{
                        position: "relative",
                        height: 160,
                        background: `center / cover no-repeat url("${displayPhoto}")`,
                    }}
                >
                    {linked && (
                        <span
                            data-testid={`cruiser-contact-linked-badge-${contact.id}`}
                            style={{
                                position: "absolute", top: 8, left: 8,
                                padding: "4px 9px",
                                borderRadius: 999,
                                fontSize: 10, fontWeight: 600,
                                letterSpacing: "0.06em",
                                background: "rgba(8, 14, 24, 0.78)",
                                border: "1px solid var(--bm-gold)",
                                color: "var(--bm-gold)",
                                backdropFilter: "blur(6px)",
                                display: "inline-flex", alignItems: "center", gap: 4,
                            }}
                        >
                            ⚓ BOSUN&apos;S MATE BOAT
                        </span>
                    )}
                    <div style={{
                        position: "absolute", left: 0, right: 0, bottom: 0,
                        height: 6,
                        background: swatch.primary,
                        borderTop: swatch.trim ? `3px solid ${swatch.trim}` : "none",
                    }} />
                </div>
            ) : (
                <>
                    <div style={{
                        height: 8,
                        background: swatch.primary,
                        borderBottom: swatch.trim ? `4px solid ${swatch.trim}` : "none",
                    }} />
                    {linked && (
                        <div
                            data-testid={`cruiser-contact-linked-badge-${contact.id}`}
                            style={{
                                margin: "10px 12px 0",
                                padding: "4px 9px",
                                borderRadius: 999,
                                fontSize: 10, fontWeight: 600,
                                letterSpacing: "0.06em",
                                background: "rgba(200, 162, 92, 0.12)",
                                border: "1px solid var(--bm-gold)",
                                color: "var(--bm-gold)",
                                display: "inline-block",
                            }}
                        >
                            ⚓ BOSUN&apos;S MATE BOAT
                        </div>
                    )}
                </>
            )}

            <div style={{ padding: "16px 18px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                    <h3 className="bm-serif" style={{
                        margin: 0, fontSize: 20, fontWeight: 500, flex: 1, lineHeight: 1.25,
                    }}>
                        {contact.boat_name}
                    </h3>
                </div>

                {(contact.boat_type || contact.boat_length_ft) && (
                    <div style={{ fontSize: 13, color: "var(--bm-text-muted)", marginBottom: 10 }}>
                        {contact.boat_type}
                        {contact.boat_length_ft ? ` · ${contact.boat_length_ft}ft` : ""}
                    </div>
                )}

                {(contact.hull_color_primary || contact.hull_color_trim) && (
                    <div style={{ fontSize: 12, color: "var(--bm-text-dim)", marginBottom: 12 }}>
                        {[contact.hull_color_primary, contact.hull_color_trim].filter(Boolean).join(" with ")} hull
                    </div>
                )}

                {people.length > 0 && (
                    <div style={{ fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                        <Users size={12} style={{ color: "var(--bm-gold)" }} />
                        <span>{people.map((p) => p.name).filter(Boolean).join(" & ")}</span>
                    </div>
                )}

                {pets.length > 0 && (
                    <div style={{ fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", gap: 6, color: "var(--bm-text-muted)" }}>
                        <Dog size={12} style={{ color: "var(--bm-gold)" }} />
                        <span>{pets.map((p) => p.name).filter(Boolean).join(", ")}</span>
                    </div>
                )}

                {contact.home_port && (
                    <div style={{ fontSize: 12, color: "var(--bm-text-dim)", marginBottom: 4 }}>
                        Home port · {contact.home_port}
                    </div>
                )}

                {contact.encounter_count > 0 && (
                    <div
                        data-testid={`cruiser-contact-encounter-count-${contact.id}`}
                        style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            marginTop: 8, padding: "3px 9px",
                            borderRadius: 999,
                            background: "rgba(200, 162, 92, 0.12)",
                            border: "1px solid var(--bm-gold-dim)",
                            fontSize: 11, color: "var(--bm-gold)",
                        }}
                    >
                        <Anchor size={10} />
                        Met {contact.encounter_count}× on the water
                    </div>
                )}

                {people.some(hasAnyContactInfo) && (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            display: "flex", flexWrap: "wrap", gap: 6,
                            marginTop: 12, paddingTop: 10,
                            borderTop: "1px solid var(--bm-border-soft)",
                        }}
                    >
                        {people.map((p, i) => hasAnyContactInfo(p) && (
                            <PersonContactRow key={i} person={p} />
                        ))}
                    </div>
                )}
            </div>
        </button>
    );
}

function hasAnyContactInfo(p) {
    return !!(p.phone || p.whatsapp || p.instagram || p.facebook || p.email);
}

function PersonContactRow({ person }) {
    const handlers = [];
    if (person.phone) handlers.push({ icon: <Phone size={12} />, label: "Call", url: `tel:${stripPhone(person.phone)}` });
    if (person.whatsapp) handlers.push({ icon: <MessageCircle size={12} />, label: "WhatsApp", url: `https://wa.me/${stripPhone(person.whatsapp)}` });
    if (person.instagram) handlers.push({ icon: <Instagram size={12} />, label: "Instagram", url: `https://instagram.com/${stripHandle(person.instagram)}` });
    if (person.facebook) handlers.push({ icon: <Facebook size={12} />, label: "Facebook", url: facebookUrl(person.facebook) });
    if (person.email) handlers.push({ icon: <Mail size={12} />, label: "Email", url: `mailto:${person.email}` });
    if (handlers.length === 0) return null;

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--bm-text-muted)" }}>
            <span style={{ marginRight: 2 }}>{person.name}:</span>
            {handlers.map((h, i) => (
                <button
                    key={i}
                    data-testid={`cc-contact-${h.label.toLowerCase()}-${person.name}`}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openExternal(h.url); }}
                    className="bm-btn bm-btn-ghost"
                    style={{ padding: "4px 7px", fontSize: 11 }}
                    title={h.label}
                    aria-label={`${h.label} ${person.name}`}
                >
                    {h.icon}
                </button>
            ))}
        </div>
    );
}

function stripPhone(s) { return (s || "").replace(/[^\d+]/g, ""); }
function stripHandle(s) { return (s || "").replace(/^@/, "").trim(); }
function facebookUrl(s) {
    const t = (s || "").trim();
    if (/^https?:\/\//i.test(t)) return t;
    return `https://facebook.com/${t.replace(/^@/, "")}`;
}

function EmptyState({ onAdd }) {
    return (
        <div data-testid="cruiser-contacts-empty" style={{
            border: "1px dashed var(--bm-border-soft)",
            borderRadius: 12, padding: "44px 24px",
            textAlign: "center", maxWidth: 560, margin: "0 auto",
        }}>
            <Anchor size={28} style={{ color: "var(--bm-gold)", marginBottom: 14 }} />
            <h3 className="bm-serif" style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 500 }}>
                Your logbook of cruising friends is empty
            </h3>
            <p style={{ margin: "0 0 22px", color: "var(--bm-text-muted)", fontSize: 14, lineHeight: 1.55 }}>
                Add the boats you&rsquo;ve crossed paths with. Hull colours, crew names,
                pet quirks — whatever helps you remember &ldquo;the Shamrock with the green trim
                we rafted up with at Princess Louisa.&rdquo;
            </p>
            <button
                data-testid="cruiser-contacts-empty-add-btn"
                onClick={onAdd}
                className="bm-btn bm-btn-gold"
                style={{ padding: "12px 22px" }}
                type="button"
            >
                <Plus size={14} /> Add your first contact
            </button>
        </div>
    );
}
